import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { quizService } from '../services/apiService'

const TakeQuiz = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quiz, setQuiz] = useState(null)
  const [attempt, setAttempt] = useState(null)
  const [answers, setAnswers] = useState({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const autoSubmittedRef = useRef(false)

  const submitQuiz = useCallback(
    async (requireConfirmation = true) => {
      if (!attempt || submitting) {
        return
      }

      if (
        requireConfirmation &&
        !window.confirm(
          'Are you sure you want to submit? You cannot change your answers after submission.',
        )
      ) {
        return
      }

      setSubmitting(true)
      try {
        await quizService.submitQuiz(attempt.id, { answers })
        alert('Quiz submitted successfully!')
        navigate('/quizzes')
      } catch (error) {
        console.error('Error submitting quiz:', error)
        alert('Failed to submit quiz. Please try again.')
        setSubmitting(false)
      }
    },
    [answers, attempt, navigate, submitting],
  )

  const handleSubmit = useCallback(
    (e) => {
      if (e) {
        e.preventDefault()
      }
      submitQuiz(true)
    },
    [submitQuiz],
  )

  const fetchQuizAndStart = useCallback(async () => {
    try {
      // Fetch quiz details
      const quizRes = await quizService.getQuiz(id)
      setQuiz(quizRes.data)

      // Start or resume attempt
      const attemptRes = await quizService.startQuiz(id)
      const attemptData = attemptRes.data
      setAttempt(attemptData)

      // Calculate time remaining
      if (!attemptData.submitted_at) {
        const startTime = new Date(attemptData.started_at)
        const now = new Date()
        const elapsedSeconds = Math.floor((now - startTime) / 1000)
        const totalSeconds = quizRes.data.time_limit * 60
        const remaining = Math.max(0, totalSeconds - elapsedSeconds)
        setTimeRemaining(remaining)
        autoSubmittedRef.current = false

        // Load existing answers if resuming
        if (attemptData.answers && typeof attemptData.answers === 'object') {
          setAnswers(attemptData.answers)
        }
      }
    } catch (error) {
      console.error('Error fetching quiz:', error)
      alert('Failed to load quiz')
      navigate('/quizzes')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchQuizAndStart()
  }, [fetchQuizAndStart])

  useEffect(() => {
    if (!attempt || attempt.submitted_at || timeRemaining === null) {
      return
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          if (!autoSubmittedRef.current) {
            autoSubmittedRef.current = true
            submitQuiz(false)
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [attempt, submitQuiz, timeRemaining])

  const handleAnswerChange = (questionId, answerId) => {
    setAnswers({
      ...answers,
      [questionId]: answerId,
    })
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  if (!quiz || !attempt) {
    return null
  }

  if (attempt.submitted_at) {
    // Show results
    return (
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='bg-white rounded-lg shadow-lg p-8'>
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-4'>
              Quiz Completed!
            </h1>
            <div className='inline-block'>
              <div
                className={`text-6xl font-bold mb-2 ${
                  attempt.passed ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {attempt.score?.toFixed(1)}%
              </div>
              <p className='text-gray-600'>
                {attempt.passed ? 'Passed' : 'Failed'} (Passing:{' '}
                {quiz.passing_score}%)
              </p>
            </div>
          </div>

          <div className='border-t pt-6'>
            <h2 className='text-xl font-semibold mb-4'>Summary</h2>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <p className='text-gray-600'>Started</p>
                <p className='font-medium'>
                  {new Date(attempt.started_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className='text-gray-600'>Submitted</p>
                <p className='font-medium'>
                  {new Date(attempt.submitted_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className='mt-8 flex justify-center space-x-4'>
            <Link
              to='/quizzes'
              className='bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700'
            >
              Back to Quizzes
            </Link>
            <button
              onClick={() => window.location.reload()}
              className='bg-gray-200 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-300'
            >
              Retake Quiz
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      {/* Header with timer */}
      <div className='bg-white rounded-lg shadow-lg p-6 mb-6 sticky top-4 z-10'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900'>{quiz.title}</h1>
            <p className='text-sm text-gray-600 mt-1'>
              {quiz.questions?.length || 0} questions
            </p>
          </div>
          <div className='text-right'>
            <div
              className={`text-3xl font-bold ${
                timeRemaining < 300 ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {formatTime(timeRemaining)}
            </div>
            <p className='text-sm text-gray-600'>Time Remaining</p>
          </div>
        </div>
      </div>

      {/* Questions */}
      <form onSubmit={handleSubmit} className='space-y-6'>
        {quiz.questions && quiz.questions.length > 0 ? (
          quiz.questions.map((question, index) => (
            <div key={question.id} className='bg-white rounded-lg shadow p-6'>
              <div className='flex items-start mb-4'>
                <span className='inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary-100 text-primary-600 font-semibold mr-3 flex-shrink-0'>
                  {index + 1}
                </span>
                <div className='flex-1'>
                  <h3 className='text-lg font-medium text-gray-900'>
                    {question.question_text}
                  </h3>
                  <p className='text-sm text-gray-500 mt-1'>
                    {question.points}{' '}
                    {question.points === 1 ? 'point' : 'points'}
                  </p>
                </div>
              </div>

              <div className='space-y-3 ml-11'>
                {question.answers?.map((answer) => (
                  <label
                    key={answer.id}
                    className='flex items-start p-3 rounded-md border-2 border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors'
                  >
                    <input
                      type='radio'
                      name={`question-${question.id}`}
                      value={answer.id}
                      checked={answers[question.id] === answer.id}
                      onChange={() =>
                        handleAnswerChange(question.id, answer.id)
                      }
                      className='mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500'
                      required
                    />
                    <span className='ml-3 text-gray-700'>
                      {answer.answer_text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-yellow-400'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm text-yellow-700'>
                  This quiz has no questions yet. Please contact your
                  instructor.
                </p>
              </div>
            </div>
          </div>
        )}

        {quiz.questions && quiz.questions.length > 0 && (
          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center justify-between'>
              <p className='text-sm text-gray-600'>
                Answer all questions before submitting
              </p>
              <div className='flex space-x-4'>
                <Link
                  to='/quizzes'
                  className='px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50'
                >
                  Cancel
                </Link>
                <button
                  type='submit'
                  disabled={submitting}
                  className='bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50'
                >
                  {submitting ? 'Submitting...' : 'Submit Quiz'}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}

export default TakeQuiz
